import {
  Box,
  Heading,
  IconCritical,
  IconDocumentBroken,
  IconFlag,
  IconStatistics,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import type { ReactNode } from 'react';

import { toDP } from '../utilities.ts';

import { Difference } from './Difference.tsx';
import * as styles from './HealthColumn.css';

import { LoadingPlaceholder } from '#src/components/LoadingPlaceholder/LoadingPlaceholder.tsx';

export type Type =
  | 'clientErrors'
  | 'clientErrorRate'
  | 'serverErrors'
  | 'totalRequests';

type TypeSettings = {
  compareByPercentage?: boolean;
  places: number;
  suffix?: string;
  icon: () => ReactNode;
  tone?: 'critical' | 'neutral' | 'positive';
};

const settings: Record<Type, TypeSettings> = {
  clientErrors: {
    compareByPercentage: true,
    places: 0,
    icon: () => <IconFlag size="small" />,
  },
  clientErrorRate: {
    places: 2,
    suffix: '%',
    icon: () => <IconCritical size="small" />,
  },
  serverErrors: {
    compareByPercentage: true,
    places: 0,
    icon: () => <IconDocumentBroken size="small" />,
  },
  totalRequests: {
    compareByPercentage: true,
    places: 0,
    icon: () => <IconStatistics size="small" />,
    tone: 'neutral',
  },
};

const signToTone = (sign: number): 'neutral' | 'critical' | 'positive' => {
  if (sign === 0) {
    return 'neutral';
  }

  return sign > 0 ? 'critical' : 'positive';
};

export const computeDifference = ({
  value,
  priorValue,
  type,
}: {
  value: number;
  priorValue: number;
  type: Type;
}) => {
  const {
    compareByPercentage,
    places,
    suffix,
    tone: explicitTone,
  } = settings[type];

  const diff = value - priorValue;
  const diffPercentage = (diff / priorValue) * 100;

  const effectiveCompareByPercentage =
    compareByPercentage && Number.isFinite(diffPercentage);

  const valueForComparison = effectiveCompareByPercentage
    ? diffPercentage
    : diff;

  const effectivePlaces = Number.isFinite(diffPercentage) ? places : 0;
  const effectivelyZero =
    Math.round(valueForComparison * 10 ** effectivePlaces) === 0;
  const effectiveValue = effectivelyZero ? 0 : valueForComparison;

  const signum = Math.sign(effectiveValue);

  const effectiveSuffix = effectiveCompareByPercentage ? '%' : (suffix ?? '');
  const formattedValue = toDP(Math.abs(effectiveValue), effectivePlaces);

  return {
    tone: explicitTone ?? signToTone(signum),
    signum,
    formatted: `${formattedValue}${effectiveSuffix}`,
  };
};

export const HealthColumn = ({
  description,
  value,
  priorValue,
  timePeriod,
  type,
}: {
  description: ReactNode;
  value: number;
  priorValue: number;
  timePeriod: string;
  type: Type;
}) => {
  const diff = computeDifference({
    value,
    priorValue,
    type,
  });

  const { places, suffix, icon: Icon } = settings[type];

  return (
    <Stack space="small" align="left">
      <Inline space="xsmall" alignY="center">
        <Box
          borderRadius="standard"
          className={styles.healthIcon}
          padding="xxsmall"
        >
          <Icon />
        </Box>

        <Stack space="xsmall">
          <Heading level="4">
            {toDP(value, places)}
            {suffix ?? ''}
          </Heading>

          <Text size="small" align="center">
            {description}
          </Text>
        </Stack>
      </Inline>

      <Box
        title={`Previously: ${toDP(priorValue, places)}${suffix ? suffix : ''}`}
      >
        <Text size="small">
          <Box component="span" className={styles.tone[diff.tone]}>
            <Difference signum={diff.signum} formatted={diff.formatted} />
          </Box>{' '}
          {timePeriod} ago
        </Text>
      </Box>
    </Stack>
  );
};

export const HealthColumnPlaceholder = ({
  description,
  timePeriod,
  type,
}: {
  description: ReactNode;
  timePeriod: string;
  type: Type;
}) => {
  const Icon = settings[type].icon;

  return (
    <Stack space="small" align="left">
      <Inline space="xsmall" alignY="center">
        <LoadingPlaceholder>
          <Box
            borderRadius="standard"
            className={styles.healthIcon}
            padding="xxsmall"
          >
            <Icon />
          </Box>
        </LoadingPlaceholder>

        <Stack space="xsmall">
          <LoadingPlaceholder>
            <Heading level="4">12345</Heading>
          </LoadingPlaceholder>

          <LoadingPlaceholder>
            <Text size="small" align="center">
              {description}
            </Text>
          </LoadingPlaceholder>
        </Stack>
      </Inline>
      <LoadingPlaceholder>
        <Text size="small">
          <Difference signum={1} formatted="123" /> {timePeriod} ago
        </Text>
      </LoadingPlaceholder>
    </Stack>
  );
};
